import React, {useState, useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {useStores} from "../../Hooks";
import {Collapse, Input, Popover, Button} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DownCaret from "../../assets/live-svg/down-caret";
import RightCaret from "../../assets/live-svg/right-caret";
import {escapeRegExp} from "../../serverClientShared/sharedUtils";
import MenuItem from "@mui/material/MenuItem";
import EllipsisContainer from "../Shared/EllipsisContainer";
import Menu from "@mui/material/Menu";
import Utility from "../../Utility";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import SvgShapes from "../../assets/live-svg/shapes.svg";
import ConditionalComponent from "../Shared/ConditionalComponent";

const nestPadding = 20;

const FindProcedureAttachmentModal = observer(() => {
    const {generalStore, modalStore, projectStore, dataStore, procedureStore} = useStores();

    const [folderMap, setFolderMap] = useState(new Map());
    const [procedureMap, setProcedureMap] = useState(new Map());
    const [selectedItemId, setSelectedItemId] = useState("");
    const [searchVal, setSearchVal] = useState('');
    const [searchListOpen, setSearchListOpen] = useState(false);
    const [autocompleteAnchor, setAutocompleteAnchor] = useState(null);
    const [autoScroll, setAutoScroll] = useState('');
    const [sendHovered, setSendHovered] = useState(false);
    const [menuSelection, setMenuSelection] = useState('favorites');

    const searchAnchorRef = useRef(null);

    useEffect(() => {
        if (!!autoScroll && autoScroll === selectedItemId) {
            const element = document.getElementById(`attachment-list-item-${selectedItemId}`);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [autoScroll, selectedItemId]);

    function updateProjectTree(populatedFolderMap, projectId, project, populatedProcedureMap) {
        populatedFolderMap.set(projectId, {
            folderId: projectId,
            open: false,
            folderName: project.ProjectName
        });

        const projectFolderMap = dataStore.projectsFoldersMap.get(projectId);
        !!projectFolderMap && projectFolderMap.forEach(folder => {
            const folderId = folder.FolderIdentifier;
            populatedFolderMap.set(folderId, {
                folderId,
                open: false,
                folderName: folder.FolderName,
                projectRef: projectId,
                parentFolder: !folder.ParentFolder || folder.ParentFolder === 'root' ? projectId : folder.ParentFolder
            });
        });

        const projectProcedureMap = dataStore.projectsProceduresMap.get(projectId);
        !!projectFolderMap && !!projectProcedureMap && projectProcedureMap.forEach(procedure => {
            if (!!procedure.IsPublished && (!procedure.ParentFolder || procedure.ParentFolder === 'root' || projectFolderMap.has(procedure.ParentFolder)) && !procedure.IsDeleted) {
                const procedureId = procedure.ProcedureIdentifier;
                populatedProcedureMap.set(procedureId, {
                    procedureId,
                    procedureType: Number(procedure.ProcedureType),
                    procedureName: procedure.ProcedureName,
                    parentFolder: !procedure.ParentFolder || procedure.ParentFolder === 'root' ? projectId : procedure.ParentFolder,
                    projectRef: projectId,
                    projectName: project.ProjectName,
                });
            }
        });
    }

    const onEnter = async () => {
        const populatedFolderMap = new Map();
        const populatedProcedureMap = new Map();

        dataStore.myProjectsMap.forEach((project, projectId) => {
            updateProjectTree(populatedFolderMap, projectId, project, populatedProcedureMap);
        });

        setFolderMap(populatedFolderMap);
        setProcedureMap(populatedProcedureMap);
        setSearchVal('');
        setSelectedItemId('');
        setAutoScroll('');
        setSendHovered(false);
    }

    const closePopover = () => {
        setFolderMap(new Map());
        setProcedureMap(new Map());
        setMenuSelection('')
        setSearchVal('');
        setSelectedItemId('');
        setAutoScroll('');
        setSendHovered(false);
        modalStore.isFindProcedureAttachmentOpen = false;
    }

    const onSearchInputTyped = e => {
        const newSearchVal = e.target.value;
        setSearchVal(newSearchVal);

        if (!newSearchVal || newSearchVal.length < 3) {
            closeSearchList();
        } else {
            openSearchList()
        }
    }

    const closeSearchList = () => {
        setSearchListOpen(false);
        setAutocompleteAnchor(null);
    }

    const openSearchList = () => {
        setSearchListOpen(true);
        setAutocompleteAnchor(searchAnchorRef.current);
    }

    const goToSearchItem = (item, itemId, isFolder) => {
        const parentFolderId = item?.parentFolder || '';
        recursivelyOpenFolders(folderMap, isFolder ? itemId : parentFolderId);
        setAutoScroll(itemId);
        setSelectedItemId(itemId);
        setMenuSelection(item.projectRef);
        closeSearchList();
    }

    const buildSearchResults = () => {
        if (searchVal.length < 3) {
            return null
        }
        const menuItems = [];

        const searchLower = escapeRegExp(searchVal.toLowerCase().trim());
        const regexSearchStr = new RegExp(searchLower, "i");

        [...folderMap.values(), ...procedureMap.values()]
            .sort((a, b) =>
                (a.folderName || a.procedureName || '').toLowerCase() > (b.folderName || b.procedureName || '').toLowerCase() ? 1 : -1
            )
            .forEach(item => {
                const itemName = item?.folderName || item?.procedureName || '';
                const itemId = item?.folderId || item?.procedureId || '';
                const isFolder = !!item?.folderId;
                if (!!itemName && itemName.search(regexSearchStr) >= 0) {
                    menuItems.push(
                        <MenuItem
                            onClick={() => goToSearchItem(item, itemId, isFolder)}
                            className='search-menu-item search-procedure-attachment'
                            key={`search-menu-item-${itemId}`}
                        >
                            <EllipsisContainer value={itemName}>
                                <div className='search-menu-item-start'>
                                    {isFolder
                                        ? <span><FolderOpenIcon/> {itemName}</span>
                                        : itemName
                                    }
                                </div>
                            </EllipsisContainer>
                        </MenuItem>
                    )
                }
            });

        return menuItems;
    }

    const renderSearchInput = () => {
        const menuItems = buildSearchResults();
        return (
            <>
                <Input
                    className="search-procedure-attachments"
                    placeholder={generalStore.translate('searchInProject')}
                    value={searchVal}
                    onChange={onSearchInputTyped}
                    disableUnderline
                />
                <div ref={searchAnchorRef} className='search-anchor'/>
                <Menu
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: generalStore.langDir === 'ltr' ? 'left' : 'right',
                    }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableAutoFocusItem
                    open={searchListOpen && !!menuItems?.length}
                    onClose={closeSearchList}
                    anchorEl={autocompleteAnchor}
                    variant='menu'
                    autoFocus={false}
                    PaperProps={{
                        className: 'autocomplete-menu attachment-search special-scroll'
                    }}
                >
                    {menuItems}
                </Menu>
            </>
        )
    }

    const handleToggleFolderOpen = folderId => {
        const newFolderMap = new Map(folderMap);
        if (newFolderMap.get(folderId).open) {
            recursivelyCloseFolders(newFolderMap, folderId)
        } else newFolderMap.get(folderId).open = true;
        setFolderMap(newFolderMap);
    };

    const recursivelyOpenFolders = (folderMap, folderId) => {
        if (!folderId) {
            return
        }

        const currentFolder = folderMap.get(folderId);
        if (!currentFolder || !!currentFolder.open) {
            return
        }

        currentFolder.open = true;
        folderMap.set(folderId, currentFolder);

        recursivelyOpenFolders(folderMap, currentFolder.parentFolder);
    }

    const recursivelyCloseFolders = (folderMap, folderId) => {
        if (!folderId) {
            return
        }

        const currentFolder = folderMap.get(folderId);
        if (!currentFolder) {
            return
        }

        currentFolder.open = false;
        folderMap.set(folderId, currentFolder);

        const folders = getDirectoryFolders(folderId);
        folders.forEach(folder => recursivelyCloseFolders(folderMap, folder.folderId));
    };

    const getDirectoryFolders = folderId => {
        const folders = Array.from(folderMap.values());
        return folders.filter(folder => folder.parentFolder === folderId)
    };

    const getDirectoryProcedures = folderId => {
        const procedures = Array.from(procedureMap.values());
        return procedures.filter(p => p.parentFolder === folderId)
    };

    const sendProcedure = procedure => {
        modalStore.modalData.sendProcedure(procedure).catch(console.error);
        closePopover();
    }

    const renderProcedureListItem = (folderId, nestLevel, procedure) => {
        const containerWidth = 367, iconPad = 8, iconWidth = 24, textPad = 8, buttonWidth = 64;
        const isSelected = selectedItemId === procedure.procedureId;
        const nameMaxWidth = containerWidth - iconPad - iconWidth - textPad - nestPadding * (nestLevel + 1) - (isSelected ? buttonWidth : 0);
        return (
            <ListItem
                button
                key={`${folderId}-${procedure.procedureId}`}
                role={undefined}
                onClick={() => setSelectedItemId(procedure.procedureId)}
                style={{paddingLeft: 4 + nestPadding * (nestLevel + 1)}}
                className={'procedure-list-item' + (isSelected ? " selected" : "")}
                id={`attachment-list-item-${procedure.procedureId}`}
                onBlur={() => !sendHovered && setSelectedItemId('')}
            >
                <div className='name-icon-container'>
                    {Utility.renderProcedureIconByType(procedure.procedureType)}
                    <EllipsisContainer value={procedure.procedureName}>
                        <div className='list-item-name' style={{maxWidth: nameMaxWidth}}>
                            {procedure.procedureName}
                        </div>
                    </EllipsisContainer>
                </div>
                <ConditionalComponent if={isSelected}>
                    <Button
                        className="send-button"
                        variant="contained"
                        color="primary"
                        onClick={() => sendProcedure(procedure)}
                        onMouseEnter={() => setSendHovered(true)}
                        onMouseLeave={() => setSendHovered(false)}
                    >
                        {generalStore.translate('send')}
                    </Button>
                </ConditionalComponent>
            </ListItem>
        )
    }

    async function selectProject(projectId) {
        try {
            await Promise.all([
                projectStore.addProjectProcedures(projectId),
                projectStore.addProjectFolders(projectId)
            ]);
        } catch (e) {
            console.error(e);
            modalStore.showErrorMessage('couldNotGetProjectData');
        }

        const project = dataStore.myProjectsMap.get(projectId);
        if (project) {
            updateProjectTree(folderMap, projectId, project, procedureMap);
            setFolderMap(new Map(folderMap));
            setProcedureMap(new Map(procedureMap));
        }

        setMenuSelection(projectId)
    }

    const renderFolder = (folderId, nestLevel) => {
        const folder = folderMap.get(folderId);
        if (!folder) {
            return
        }

        const containerWidth = 367, iconPad = 8, iconWidth = 24, textPad = 8, caretWidth = 10;
        const nameMaxWidth = containerWidth - iconPad - iconWidth - textPad - caretWidth - nestPadding * (nestLevel + 1);

        return (
            <React.Fragment key={folderId + nestLevel}>
                <ListItem
                    button
                    role={undefined}
                    key={'list-item-' + folderId + nestLevel}
                    style={{paddingLeft: 4 + nestPadding * nestLevel}}
                    className={`folder-list-item${selectedItemId === folderId ? " selected" : ""}`}
                    id={`attachment-list-item-${folderId}`}
                    onClick={() => handleToggleFolderOpen(folderId)}
                    onBlur={() => setSelectedItemId('')}
                >
                    {folder?.open
                        ? <ListItemIcon>
                            <DownCaret color="#36415D" width={20} height={12.5}/>
                        </ListItemIcon>
                        : <ListItemIcon className='right-caret'>
                            <RightCaret color="#36415D" width={12.5} height={20}/>
                        </ListItemIcon>
                    }
                    <FolderOpenIcon color="primary"/>

                    <EllipsisContainer value={folder.folderName}>
                        <div className='list-item-name' style={{maxWidth: nameMaxWidth}}>
                            {folder.folderName}
                        </div>
                    </EllipsisContainer>
                </ListItem>
                <Collapse in={!!folder?.open} timeout="auto" unmountOnExit>
                    {getDirectoryFolders(folderId).map(f => renderFolder(f.folderId, nestLevel + 1))}
                    {getDirectoryProcedures(folderId).map(procedure => renderProcedureListItem(folderId, nestLevel, procedure))}
                </Collapse>
            </React.Fragment>
        )
    };

    const renderMainMenu = () => {
        if (dataStore.isLoadingProjects) {
            return <div>Loading projects...</div>;
        }

        return (
            <div className='menu-container'>
                <div className='menu-list special-scroll'>
                    <div
                        className={`menu-item${menuSelection === 'favorites' ? ' selected' : ''}`}
                        onClick={() => setMenuSelection('favorites')}
                    >
                        <StarOutlinedIcon/> {generalStore.translate('myFavorites')}
                    </div>
                    {[...dataStore.myProjectsMap.values()].map(project => {
                        return (
                            <div
                                key={project.ProjectIdentifier}
                                className={`menu-item${menuSelection === project.ProjectIdentifier ? ' selected' : ''}`}
                                onClick={() => selectProject(project.ProjectIdentifier)}
                            >
                                <SvgShapes
                                    width={24}
                                    height={24}
                                    color={menuSelection === project.ProjectIdentifier ? '#36415D' : "#7F7F7F"}
                                />
                                <EllipsisContainer value={project.ProjectName}>
                                    <div className='project-name'>
                                        {project.ProjectName}
                                    </div>
                                </EllipsisContainer>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    const renderFindProcedureAttachmentBody = () => {
        return (
            <div className="find-procedure-attachment-body">
                {renderMainMenu()}
                <div className='find-procedure-attachment'>
                    {renderSearchInput()}
                    <List
                        component="div"
                        className="find-procedure-attachment-list special-scroll"
                    >
                        {menuSelection === 'favorites'
                            ? <>
                                {[...procedureStore.favoriteProcedures.values()].map(procedure => {
                                    const formattedProcedure = {
                                        procedureId: procedure.ProcedureIdentifier,
                                        procedureName: procedure.ProcedureName,
                                        procedureType: procedure.ProcedureType,
                                        parentFolder: 'favorites',
                                        projectRef: procedure.ProjectRef,
                                        projectName: procedure.ProjectName,
                                    }
                                    return renderProcedureListItem('favorites', -1, formattedProcedure)
                                })}
                            </>
                            : <>
                                {getDirectoryFolders(menuSelection).map(folder =>
                                    renderFolder(folder.folderId, 0)
                                )}
                                {getDirectoryProcedures(menuSelection).map(procedure =>
                                    renderProcedureListItem(menuSelection, -1, procedure)
                                )}
                            </>
                        }
                    </List>
                </div>
            </div>
        )
    }

    useEffect(() => {
        if (dataStore.myProjectsMap.size === 0) {
            dataStore.fetchProjectsIfEmpty();
        }
        // eslint-disable-next-line
    }, []);

    return (
        <Popover
            open={!!modalStore.isFindProcedureAttachmentOpen}
            onClose={closePopover}
            anchorReference={'none'}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            className={`find-procedure-attachment-popover${Utility.isTablet() ? ' tablet' : ''}`}
            // PaperProps={{className: 'dialog-paper'}}
            TransitionProps={{
                onEnter
            }}
        >
            {renderFindProcedureAttachmentBody()}
        </Popover>
    );
})

export default FindProcedureAttachmentModal;
